import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { userService } from '../../../services/user.service';
import { useTranslation } from 'react-i18next';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import Alert from '../../UI/Alert/Alert';
import styles from './UserEditForm.module.css';
import { toast } from 'react-toastify';

function UserEditForm({ isOpen, onClose, onSuccess, userId, initialData }) {
    const { t: tCommon } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');

    const [email, setEmail] = useState({ value: '', error: null });
    const [role, setRole] = useState({ value: '', error: null });
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const roles = useMemo(() => [
        { value: 'admin', label: tCommon('users.roles.admin') },
        { value: 'inventor', label: tCommon('users.roles.inventor') },
        { value: 'expert', label: tCommon('users.roles.expert') }
    ], [tCommon]);


    useEffect(() => {
        if (isOpen && initialData) {
            setEmail({ value: initialData.email || '', error: null });
            const initialRole = roles.find(role => role.label === initialData.code);
            setRole({ value: initialRole?.value || '', error: null });
            setGeneralError(null);
        }
    }, [isOpen, initialData, roles]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setEmail({ value: email.value, error: null });
        setRole({ value: role.value, error: null });
        setGeneralError(null);

        try {
            const response = await userService.editUser(userId, {
                email: email.value,
                code: role.value,
            });

            if (response.status === 'success' || response.user) {
                setEmail({ value: '', error: null });
                setRole({ value: '', error: null });
                setGeneralError(null);

                toast.success(tValidation('user_edited_successfully'));
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach(error => {
                    if (error.path[0] === 'email') {
                        setEmail({ value: email.value, error: error });
                    }
                    if (error.path[0] === 'code') {
                        setRole({ value: role.value, error: error });
                    }
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity.toString());
                setGeneralError(message);
                toast.error(message);
            } else {
                const message = error.message || 'Something went wrong';
                setGeneralError(message);
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setEmail({ value: '', error: null });
            setRole({ value: '', error: null });
            setGeneralError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className={styles.overlay}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{tCommon('users.editUser.title')}</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={isLoading}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {generalError && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}
                    <Input
                        type="email"
                        placeholder={tCommon('users.editUser.email')}
                        value={email.value}
                        onChange={(e) => setEmail({ value: e.target.value, error: null })}
                        error={email.error}
                        disabled
                    />
                    <Select
                        options={roles}
                        value={role.value}
                        onChange={(e) => setRole({ value: e.target.value, error: null })}
                        placeholder={tCommon('users.editUser.role')}
                        error={role.error}
                        required
                    />
                    <div className={styles.buttons}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            {tCommon('users.editUser.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {tCommon('users.editUser.save')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
        , document.body);
}

export default UserEditForm;
